"use client";

import React, { useEffect, useState } from "react";
import { useTokenStore } from "@/lib/zustand";
import { useTokensForChain } from "@/hooks/use-tokens-for-chain";
import { SupportedChain } from "@/hooks/get-tokens-and-chains";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { ChevronRightIcon, ChevronLeftIcon } from "lucide-react";
import { Separator } from "../ui/separator";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { useSupportedChains } from "@/hooks/use-supported-chains";
import { ChainId, ChainKey, ChainType } from "@lifi/sdk";
import { useInView } from "react-intersection-observer";
import { Skeleton } from "../ui/skeleton";
import { useChainId, useSwitchChain, useConnectors } from "wagmi";
import { useParaAccount } from "@/hooks/use-appkit-account";

const FEATURED_TOKEN = {
  chainId: ChainId.SOL,
  address: "LSZmgFnezyfSyWW5xn6dc8fC253cnwYTGgf1TZLGapE",
  symbol: "DEFAPES",
  name: "Defapes",
  decimals: 6,
  logoURI: "https://node1.irys.xyz/4uGuZVFPEZwXspWd_055cZsRIcDdA9JVQa5gt8kkNv0",
  priceUSD: "0",
};

const CHAIN_PRIORITY: Record<number, number> = {
  1151111081099710: 0,
  1: 1,
  56: 2,
  8453: 3,
  137: 4,
  42161: 5,
  10: 6,
  43114: 7,
  59144: 8,
  534352: 9,
  81457: 10,
  5000: 11,
  100: 12,
  250: 13,
};

const getChainTicker = (chain: SupportedChain) => {
  if (chain.id === 1151111081099710) return "SOL";
  if (chain.id === 1) return "ETH";
  if (chain.id === 56) return "BNB";
  if (chain.id === 8453) return "BASE";
  if (chain.id === 137) return "POL";
  if (chain.id === 42161) return "ARB";
  if (chain.id === 10) return "OP";
  if (chain.id === 43114) return "AVAX";

  return chain.name.slice(0, 4).toUpperCase();
};

export const SelectTokenModal = ({ type }: { type: "receive" | "send" }) => {
  const {
    setToToken,
    toToken,
    setFromToken,
    fromToken,
    setToChain,
    setFromChain,
  } = useTokenStore();

  // Current connected chain
  const chainId = useChainId();

  const { switchChain } = useSwitchChain();
  const connectors = useConnectors();
  // Find Para connector to force chain switch through Para instead of Phantom
  const paraConnector = connectors.find(c => c.id === 'para');

  // Handle wallet alert switch
  const { caipAddress, isConnected } = useParaAccount();

  // Search chain
  const [chainSearch, setChainSearch] = useState("");

  // Chain container ref
  const chainContainerRef = React.useRef<HTMLDivElement>(null);

  // Fetch supported chains
  const {
    data: supportedChains,
    isLoading: isLoadingSupportedChains,
    isError: isErrorSupportedChains,
  } = useSupportedChains({
    search: chainSearch,
  });

  if (isErrorSupportedChains) {
    console.error("Chain fetch failed:", isErrorSupportedChains);
  }

  const sortedChains = [...(supportedChains?.chains ?? [])].sort((a, b) => {
    const priorityA = CHAIN_PRIORITY[a.id] ?? Number.MAX_SAFE_INTEGER;
    const priorityB = CHAIN_PRIORITY[b.id] ?? Number.MAX_SAFE_INTEGER;

    return priorityA - priorityB;
  });

  // Ref point for viewing
  const { ref: bottomRef, inView } = useInView();
  const closeRef = React.useRef<HTMLButtonElement>(null);
  const tokenListRef = React.useRef<HTMLDivElement>(null);

  // Set a temp chain
  const [tempChain, setTempChain] = useState<SupportedChain>(() => {
    return {
      id: ChainId.SOL,
      name: "Solana",
      logoURI:
        "https://raw.githubusercontent.com/lifinance/types/main/src/assets/icons/chains/solana.svg",
      chainType: ChainType.SVM,
      symbol: ChainKey.SOL,
    };
  });

  // Fetch tokens for chain
  const [tokenSearch, setTokenSearch] = useState("");
  const [isSwitchingChain, setIsSwitchingChain] = useState(false);
  const {
    data: tokensForChain,
    isLoading: isLoadingTokens,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTokensForChain({
    chainId: tempChain.id,
    search: tokenSearch,
  });

  const scrollChains = (direction: "right" | "left") => {
    if (chainContainerRef.current) {
      const scrollAmount = 48 + 14; // item width (48px) + gap (14px)
      const currentScroll = chainContainerRef.current.scrollLeft;
      chainContainerRef.current.scrollTo({
        left:
          direction === "right"
            ? currentScroll + scrollAmount
            : currentScroll - scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // fetch more tokens when at bottom of list
  useEffect(() => {
    if (inView && !isLoadingTokens && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, isLoadingTokens, hasNextPage, fetchNextPage]);

  // reset scroll position when chain changes
  useEffect(() => {
    if (tokenListRef.current) {
      tokenListRef.current.scrollTop = 0;
    }
  }, [tempChain]);

  // Reset switching state when tokens finish loading
  useEffect(() => {
    if (!isLoadingTokens && isSwitchingChain) {
      setIsSwitchingChain(false);
    }
  }, [isLoadingTokens, isSwitchingChain]);

  return (
    <Dialog
      onOpenChange={() => {
        setTokenSearch("");
        setChainSearch("");
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" className="w-fit flex items-center gap-3 px-0">
          <Avatar className="h-8 w-8 p-1 rounded">
            <AvatarImage
              src={type === "receive" ? toToken?.logoURI : fromToken?.logoURI}
              alt={type === "receive" ? toToken?.symbol : fromToken?.symbol}
            />
            <AvatarFallback className="bg-[#2D2E33]">
              {type === "receive"
                ? toToken?.symbol.slice(0, 2).toUpperCase()
                : fromToken?.symbol.slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="text-2xl text-white font-medium">
            {type === "receive" ? toToken?.symbol : fromToken?.symbol}
          </span>
          <ChevronRightIcon className="h-5 w-5 text-white" />
        </Button>
      </DialogTrigger>
      <DialogTitle className="sr-only">Select Token</DialogTitle>
      <DialogContent className="bg-gradient" aria-describedby={undefined}>
        <DialogClose ref={closeRef} className="hidden" />
        <DialogHeader>
          <div className="flex flex-row items-center gap-4 mt-8">
            <Input
              placeholder="Search Chain"
              onChange={(e) => {
                const search = e.target.value.toLowerCase();
                setChainSearch(search);
              }}
              className="placeholder:!text-white/70"
            />
            <Input
              placeholder="Search Token or Paste Address"
              onChange={(e) => {
                setTokenSearch(e.target.value);
              }}
              className="placeholder:!text-white/70"
            />
          </div>
        </DialogHeader>
        <Separator className="my-1" />
        <div className="relative w-full h-12 overflow-hidden">
          <div
            ref={chainContainerRef}
            className="w-[80%] mx-auto flex flex-row gap-3.5 items-center justify-start overflow-x-auto scrollbar-none"
          >
            {sortedChains.length > 0 &&
              sortedChains.map((chain) => (
                <TooltipProvider key={chain.id}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="w-[48px] min-w-[48px] h-[48px] p-0 flex items-center justify-center"
                        onClick={() => {
                          setIsSwitchingChain(true);
                          setTempChain(chain);
                          // Switch chain if we're connected to the correct chain
                          // Pass Para connector explicitly to prevent Phantom interception
                          if (isConnected && chainId !== chain.id) {
                            if (paraConnector) {
                              switchChain({ chainId: chain.id, connector: paraConnector });
                            } else {
                              switchChain({ chainId: chain.id });
                            }
                          }
                        }}
                      >
                        <div className="flex flex-col items-center gap-0.5">
                          <Avatar className="h-7 w-7">
                            <AvatarImage
                              src={chain.logoURI || "/icons/chains/ethereum.png"}
                              alt={chain.name}
                            />
                            <AvatarFallback className="text-[9px]">
                              {chain.name.slice(0, 2)}
                            </AvatarFallback>
                          </Avatar>
                          <span
                            className={`text-[9px] leading-none ${
                              tempChain.name === chain.name
                                ? "text-primary"
                                : "text-[#888]"
                            }`}
                          >
                            {getChainTicker(chain)}
                          </span>
                        </div>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{chain.name}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            {isLoadingSupportedChains && (
              <div className="flex flex-col w-full gap-3.5 min-h-full">
                <Skeleton className="min-h-12 w-full rounded-md shrink-0" />
              </div>
            )}
            {!isLoadingSupportedChains &&
              supportedChains?.chains &&
              supportedChains.chains.length === 0 && (
                <p className="text-white">No chains found</p>
              )}
          </div>
          <button
            className="absolute right-0 top-1/2 -translate-y-1/2 rounded-full h-7 w-7 flex items-center justify-center bg-white"
            onClick={() => scrollChains("right")}
          >
            <ChevronRightIcon className="h-5 w-5 text-black" />
          </button>
          <button
            className="absolute left-0 top-1/2 -translate-y-1/2 rounded-full h-7 w-7 flex items-center justify-center bg-white"
            onClick={() => scrollChains("left")}
          >
            <ChevronLeftIcon className="h-5 w-5 text-black" />
          </button>
        </div>

        <Separator className="my-1" />
        <div
          ref={tokenListRef}
          className="flex flex-col gap-3.5 justify-start items-start h-96 overflow-y-auto"
        >
          {(isLoadingTokens || isSwitchingChain) && (
            <div className="flex flex-col w-full gap-3.5 min-h-full">
              {[...Array(2)].map((_, i) => (
                <Skeleton
                  key={i}
                  className="min-h-12 w-full rounded-md shrink-0"
                />
              ))}
            </div>
          )}
          {!isLoadingTokens && !isSwitchingChain && tokensForChain?.pages && (
            <>
              {/* Show featured token first if on BSC */}
              {/* {tempChain.id === ChainId.SOL && (
                <Button
                  key={`${FEATURED_TOKEN.address}-${tempChain.id}`}
                  variant="ghost"
                  className="w-full flex items-center gap-3 justify-between h-12 hover:bg-[#2D2E33] bg-opacity-50 bg-primary/60"
                  onClick={() => {
                    // Only validate chain type for "send" operations
                    if (type === "send" && isConnected && caipAddress) {
                      const isSVMConnected = caipAddress.startsWith("solana:");
                      const requiredChainType = tempChain.chainType;

                      if (
                        requiredChainType === ChainType.SVM &&
                        !isSVMConnected
                      ) {
                        alert("Please switch to an SVM-compatible wallet.");
                        return;
                      }

                      if (
                        requiredChainType === ChainType.EVM &&
                        isSVMConnected
                      ) {
                        alert("Please switch to an EVM-compatible wallet.");
                        return;
                      }
                    }

                    // Proceed with token selection
                    if (type === "receive") {
                      setToChain(tempChain);
                      setToToken(FEATURED_TOKEN);
                    } else {
                      setFromChain(tempChain);
                      setFromToken(FEATURED_TOKEN);
                    }
                    closeRef.current?.click();
                  }}
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-8 w-8 rounded-full">
                      <AvatarImage
                        src={FEATURED_TOKEN.logoURI}
                        alt={FEATURED_TOKEN.symbol}
                      />
                      <AvatarFallback className="bg-primary text-white">
                        {FEATURED_TOKEN.symbol.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start justify-start">
                      <span className="text-white">
                        {FEATURED_TOKEN.symbol}
                      </span>
                      <span className="text-sm text-gray-400">
                        {FEATURED_TOKEN.name}
                      </span>
                    </div>
                  </div>
                  <p className="text-white">Featured</p>
                </Button>
              )} */}
              {tokensForChain?.pages
                .flatMap((page) => page.tokens)
                .filter(
                  (token) =>
                    // Filter out the featured token from the regular list to avoid duplication
                    tempChain.id !== 56 ||
                    token.address.toLowerCase() !==
                      FEATURED_TOKEN.address.toLowerCase()
                )
                .map((token) => (
                  <Button
                    key={`${token.address}-${tempChain.id}`}
                    variant="ghost"
                    className="w-full flex items-center gap-3 justify-start h-12 hover:bg-[#2D2E33]"
                    // In the token selection onClick handler:
                    onClick={() => {
                      // Only validate chain type for "send" operations
                      if (type === "send" && isConnected && caipAddress) {
                        const isSVMConnected =
                          caipAddress.startsWith("solana:");
                        const requiredChainType = tempChain.chainType;
                        console.log("requiredChainType", requiredChainType);
                        console.log("isSVMConnected", isSVMConnected);
                        console.log("caipAddress", caipAddress);
                        if (
                          requiredChainType === ChainType.SVM &&
                          !isSVMConnected
                        ) {
                          // openModal()
                        }

                        if (
                          requiredChainType === ChainType.EVM &&
                          isSVMConnected
                        ) {
                          // alert("Please switch to an EVM-compatible wallet.");
                          // openModal()
                        }
                      }

                      // Proceed with token selection
                      if (type === "receive") {
                        setToChain(tempChain);
                        setToToken({ ...token });
                      } else {
                        setFromChain(tempChain);
                        setFromToken({ ...token });
                      }
                      closeRef.current?.click();
                    }}
                  >
                    <Avatar className="h-8 w-8 rounded-full">
                      <AvatarImage src={token.logoURI} alt={token.symbol} />
                      <AvatarFallback className="bg-primary text-white">
                        {token.symbol.slice(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col items-start justify-start">
                      <span className="text-white">{token.symbol}</span>
                      <span className="text-sm text-gray-400">
                        {token.name}
                      </span>
                    </div>
                  </Button>
                ))}
              <div ref={bottomRef} className="h-1 w-full invisible" />
              {isFetchingNextPage && (
                <div className="flex flex-col w-full gap-3 min-h-full">
                  <Skeleton className="min-h-12 w-full rounded-md shrink-0" />
                </div>
              )}
            </>
          )}
          {!isLoadingTokens &&
            !isSwitchingChain &&
            tokensForChain?.pages &&
            tokensForChain.pages.flatMap((page) => page.tokens).length ===
              0 && <p className="text-white">No tokens found</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
